import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cliente } from '@/lib/cliente';
import { cn } from '@/lib/utils';
import type { BcResumenObra } from '@adelante/shared';
import { formatearCRC } from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

type Col = 'obra' | 'registrado' | 'a_registrar';

/** Solo se puede reportar una obra con producción inicializada y con cambios pendientes. */
function esReportable(o: BcResumenObra): boolean {
  return o.produccion_inicializada && !o.ya_registrado;
}

export function IntegracionBcPantalla() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState<string>(hoyISO());
  const [filtro, setFiltro] = useState<string>('');
  const [orden, setOrden] = useState<{ col: Col; dir: 1 | -1 }>({ col: 'a_registrar', dir: -1 });
  const [seleccion, setSeleccion] = useState<Set<string>>(new Set());
  const [confirmando, setConfirmando] = useState(false);

  const resumenQ = useQuery({
    queryKey: ['bc-resumen'],
    queryFn: () => cliente.bc.resumen(),
  });
  const resumen = resumenQ.data?.data ?? null;

  const filas = useMemo(() => {
    let arr: BcResumenObra[] = resumen?.obras ?? [];
    const f = filtro.trim().toLowerCase();
    if (f) arr = arr.filter((o) => o.obra.toLowerCase().includes(f));
    const { col, dir } = orden;
    return [...arr].sort((a, b) => {
      if (col === 'obra') return a.obra.localeCompare(b.obra, 'es', { numeric: true }) * dir;
      const va = col === 'registrado' ? a.monto_registrado : a.monto_a_registrar;
      const vb = col === 'registrado' ? b.monto_registrado : b.monto_a_registrar;
      return (va - vb) * dir;
    });
  }, [resumen, filtro, orden]);

  const reportables = useMemo(() => filas.filter(esReportable), [filas]);
  const seleccionadas = useMemo(
    () => reportables.filter((o) => seleccion.has(o.obra)),
    [reportables, seleccion],
  );
  const todasSeleccionadas =
    reportables.length > 0 && reportables.every((o) => seleccion.has(o.obra));

  function ordenar(col: Col) {
    setOrden((o) => (o.col === col ? { col, dir: o.dir === 1 ? -1 : 1 } : { col, dir: 1 }));
  }

  function alternar(obra: string) {
    setSeleccion((prev) => {
      const next = new Set(prev);
      if (next.has(obra)) next.delete(obra);
      else next.add(obra);
      return next;
    });
  }

  function alternarTodas() {
    setSeleccion((prev) => {
      if (reportables.every((o) => prev.has(o.obra))) return new Set();
      return new Set(reportables.map((o) => o.obra));
    });
  }

  const reportarLoteM = useMutation({
    mutationFn: async () => {
      const resultados: { obra: string; ok: boolean; mensaje?: string }[] = [];
      for (const o of seleccionadas) {
        try {
          const res = await cliente.bc.reportar(o.obra);
          const fallas = (res.data?.resultados ?? []).filter((r) => !r.ok);
          resultados.push({
            obra: o.obra,
            ok: fallas.length === 0,
            mensaje: fallas[0]?.mensaje,
          });
        } catch (e) {
          resultados.push({
            obra: o.obra,
            ok: false,
            mensaje: e instanceof Error ? e.message : 'Error',
          });
        }
      }
      return resultados;
    },
    onSuccess: (resultados) => {
      const ok = resultados.filter((r) => r.ok).length;
      const fail = resultados.length - ok;
      if (fail === 0) {
        toast.success(`Reportadas ${ok} obra(s) en BC`);
      } else {
        toast.warning(`Reportadas ${ok} obra(s), ${fail} con error`);
      }
      setSeleccion(new Set());
      setConfirmando(false);
      qc.invalidateQueries({ queryKey: ['bc-resumen'] });
    },
    onError: (e) => {
      toast.error(e instanceof Error ? e.message : 'Error al reportar');
      setConfirmando(false);
    },
  });

  // Totales sobre las filas VISIBLES (respetan el filtro).
  const totReg = filas.reduce((s, o) => s + o.monto_registrado, 0);
  const totAReg = filas.reduce((s, o) => s + o.monto_a_registrar, 0);

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="font-semibold text-xl">Integración Business Central</h1>
        <p className="text-muted-foreground text-sm">
          Elegí la fecha de registro y trabajá las obras: ver monto registrado, monto a registrar y
          el detalle por partida.
        </p>
      </div>

      <Card className="flex flex-wrap items-end gap-3 p-4">
        <div className="grid gap-1">
          <Label>Fecha de registro</Label>
          <Input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="w-40"
          />
        </div>
        <div className="grid gap-1">
          <Label>Filtrar obra</Label>
          <Input
            placeholder="Ej. VN-K"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            className="w-44"
          />
        </div>
      </Card>

      {resumenQ.isLoading && (
        <p className="text-muted-foreground text-sm">Cargando obras desde BC…</p>
      )}
      {resumenQ.isError && (
        <p className="text-destructive text-sm">
          {resumenQ.error instanceof Error ? resumenQ.error.message : 'Error al cargar'}
        </p>
      )}

      {resumen && (
        <>
          <Card className="flex flex-wrap items-center justify-between gap-6 p-4 text-sm">
            <div className="flex flex-wrap gap-6">
              <div>
                <div className="text-muted-foreground text-xs">Obras</div>
                <div className="font-semibold">{filas.length}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Total registrado</div>
                <div className="font-semibold">{formatearCRC(totReg)}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">Total a registrar</div>
                <div className="font-semibold text-amber-700">{formatearCRC(totAReg)}</div>
              </div>
            </div>
            {seleccionadas.length > 0 && (
              <Button onClick={() => setConfirmando(true)}>
                Reportar seleccionadas ({seleccionadas.length})
              </Button>
            )}
          </Card>

          <Card className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={todasSeleccionadas}
                      onCheckedChange={alternarTodas}
                      disabled={reportables.length === 0}
                      aria-label="Seleccionar todas"
                    />
                  </TableHead>
                  <TableHead>
                    <button
                      type="button"
                      onClick={() => ordenar('obra')}
                      className="inline-flex items-center gap-1"
                    >
                      Obra <ArrowUpDown className="size-3" />
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button
                      type="button"
                      onClick={() => ordenar('registrado')}
                      className="inline-flex items-center gap-1"
                    >
                      Monto Registrado <ArrowUpDown className="size-3" />
                    </button>
                  </TableHead>
                  <TableHead className="text-right">
                    <button
                      type="button"
                      onClick={() => ordenar('a_registrar')}
                      className="inline-flex items-center gap-1"
                    >
                      Monto a Registrar <ArrowUpDown className="size-3" />
                    </button>
                  </TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-right" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filas.map((o) => (
                  <TableRow key={o.obra}>
                    <TableCell>
                      <Checkbox
                        checked={seleccion.has(o.obra)}
                        onCheckedChange={() => alternar(o.obra)}
                        disabled={!esReportable(o)}
                        aria-label={`Seleccionar ${o.obra}`}
                      />
                    </TableCell>
                    <TableCell className="font-mono font-semibold text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        {o.obra}
                        {o.estado === 'finalizada' && (
                          <span className="rounded bg-emerald-100 px-1 py-0.5 font-sans text-[9px] font-medium text-emerald-700">
                            Terminada
                          </span>
                        )}
                        {o.estado === 'en_espera' && (
                          <span className="rounded bg-violet-100 px-1 py-0.5 font-sans text-[9px] font-medium text-violet-600">
                            ❄ Congelada
                          </span>
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">{formatearCRC(o.monto_registrado)}</TableCell>
                    <TableCell
                      className={cn(
                        'text-right',
                        o.monto_a_registrar > 0 && 'font-semibold text-amber-700',
                      )}
                    >
                      {o.monto_a_registrar > 0 ? formatearCRC(o.monto_a_registrar) : '—'}
                    </TableCell>
                    <TableCell className="text-center text-xs">
                      {!o.produccion_inicializada ? (
                        <span className="text-rose-700">Sin inicializar</span>
                      ) : o.ya_registrado ? (
                        <span className="text-emerald-700">Al día</span>
                      ) : (
                        <span className="text-amber-700">{o.n_cambios} x reportar</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button asChild size="sm" variant="outline">
                        <Link
                          to={`/admin/integracion-bc/${encodeURIComponent(o.obra)}?fecha=${fecha}`}
                        >
                          Detalle
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground text-sm">
                      Sin obras.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </>
      )}

      <Dialog
        open={confirmando}
        onOpenChange={(open) => !reportarLoteM.isPending && setConfirmando(open)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirmar reporte a BC</DialogTitle>
            <DialogDescription>
              Se va a escribir el avance en BC (campo Quantity) para estas {seleccionadas.length}{' '}
              obra(s). Revisá los montos antes de confirmar.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-72 space-y-1 overflow-y-auto text-sm">
            {seleccionadas.map((o) => (
              <div key={o.obra} className="flex items-center justify-between border-b py-1.5">
                <span className="font-mono font-medium text-xs">{o.obra}</span>
                <span className="text-amber-700">{formatearCRC(o.monto_a_registrar)}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t pt-2 text-sm">
            <span className="font-medium">Total</span>
            <span className="font-semibold text-amber-700">
              {formatearCRC(seleccionadas.reduce((s, o) => s + o.monto_a_registrar, 0))}
            </span>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmando(false)}
              disabled={reportarLoteM.isPending}
            >
              Cancelar
            </Button>
            <Button onClick={() => reportarLoteM.mutate()} disabled={reportarLoteM.isPending}>
              {reportarLoteM.isPending ? 'Reportando…' : `Confirmar (${seleccionadas.length})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
